import { AgentRunStatusMessage } from 'features/trend-detection/constants/stale-filters'

export type RunRowVariant = 'running' | 'failed' | 'found' | 'empty'

export interface RunRowSummary {
  variant: RunRowVariant
  text: string
  timestampIso: string
  /** Second line under the primary row text. Empty when the row wasn't
   *  classified with a status_message the FE knows copy for. */
  subtitle?: string
  /** Set when the ActivityFeed should render an inline "Edit filters" link
   *  (config-class failure the user can act on). */
  showEditFiltersLink?: boolean
}

// Structural shape both IssueAgentRunResponse and BenchmarkingAgentRun satisfy.
export interface RunSummarizable {
  status: 'created' | 'running' | 'completed' | 'failed'
  completed_at: string | null
  create_time?: string
  created_at?: string
  issue_count?: number
  insight_count?: number
  /** Machine-readable code for how the run ended
   *  (`common_lib.models.agent_run_failure`); null when unclassified. */
  status_message?: string | null
}

export function summarizeRun(run: RunSummarizable): RunRowSummary {
  const timestampIso =
    run.completed_at ?? run.created_at ?? run.create_time ?? ''
  if (run.status === 'running' || run.status === 'created') {
    return {
      variant: 'running',
      text: 'Scanning recent feedback for insights…',
      timestampIso,
    }
  }
  if (run.status === 'failed') {
    const isStaleFilterFailure =
      run.status_message === AgentRunStatusMessage.InvalidFilterValues
    return {
      variant: 'failed',
      text: 'Run incomplete — no insights found',
      timestampIso,
      subtitle: isStaleFilterFailure
        ? 'Some values no longer exist in this project.'
        : undefined,
      showEditFiltersLink: isStaleFilterFailure,
    }
  }
  const count = run.insight_count ?? run.issue_count ?? 0
  if (count === 0) {
    return {
      variant: 'empty',
      text: 'No insights found this run',
      timestampIso,
    }
  }
  const plural = count === 1 ? 'insight' : 'insights'
  return {
    variant: 'found',
    text: `${count} ${plural} found`,
    timestampIso,
  }
}
